import { Box } from '@mui/material';
import { alpha } from '@mui/system';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { invalidateDemandMutationViews } from '@/api/demand/cache-invalidation';
import { _removeDemandEntry } from '@/api/demand/mutations';
import AlertTriangleIcon from '@/assets/svg/ui-elements/alert-triangle-outline-red.svg';
import { Button, Modal, Separator, theme, Typography } from '@/design-system';
import { useGlobalModal } from '@/global-hooks/use-global-modal';
import { showToast } from '@/shared/components/notification-popup';

export type DemandRemoveModalProps = {
  id: string;
  companyName: string;
};

export const DemandRemoveModal = () => {
  const { open, handleClose, props } = useGlobalModal('DEMAND_REMOVE_MODAL');
  const queryClient = useQueryClient();

  const { mutate: removeEntry, isPending } = useMutation({
    mutationFn: () => _removeDemandEntry(props?.id ?? ''),
    onSuccess: () => {
      showToast('Demand entry removed', 'success');
      void invalidateDemandMutationViews(queryClient);
      handleClose();
    },
    onError: () => showToast('Could not remove demand entry', 'error'),
  });

  return (
    <Modal
      open={open}
      onOpenChange={(next) => (!next ? handleClose() : undefined)}
      title="Remove Demand Entry"
      description="Removed entries are retained for audit purposes and cannot be re-activated."
      showClose
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          p: '24px',
          maxWidth: '578px',
        }}
      >
        <Separator />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            padding: '12px',
            borderRadius: '6px',
            background: alpha(theme.colors.error.solid, 0.1),
            border: `1px solid ${alpha(theme.colors.error.solid, 0.2)}`,
          }}
        >
          <AlertTriangleIcon width={24} height={24} style={{ flexShrink: 0 }} />
          <Typography variant="body3" color={theme.colors.text.primary}>
            Remove this demand entry for {props?.companyName ?? 'this company'}? This will mark it
            as no longer active.
          </Typography>
        </Box>

        <Separator />

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
          <Button
            color="destructive"
            variant="solid"
            size="lg"
            fullWidth
            onClick={() => {
              if (!isPending) removeEntry();
            }}
            disabled={isPending}
          >
            {isPending ? 'Removing…' : 'Remove'}
          </Button>
          <Button
            color="tertiary"
            variant="outline"
            size="lg"
            fullWidth
            onClick={handleClose}
            disabled={isPending}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};
